import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

/* TRANSLATE */
import { useTranslation } from "react-i18next";
/* ICONS */
import CloseIcon from '@mui/icons-material/Close';
/* LAB */
import { LoadingButton } from '@mui/lab';
/* REQUEST */
import { getUserById, getPermissions, assignExtraPermissions } from 'utils/Users';


function intersection(a, b) {
    return a.filter((value) => b.includes(value));
}


const ExtraPermission = ({ open, setOpen, setAlert, setOpenAlert, setContextMenu, reloading, selected, setSelectedRow }) => {

    const { t } = useTranslation();
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        const request = async () => {
            try {
                setLoading(true);
                const [users, permissions] = await Promise.allSettled([
                    getUserById(setLoading, selected[0]),
                    getPermissions(setLoading),
                ]);

                if(users.status === 'fulfilled') {
                    const userData = users.value?.data || [];
                    //filtrar solo los extra_permissions que esten con is_aggregation: true

                    // Filtramos los permisos activos y mapeamos para incluir la información del permiso
                    const activeExtraPermissions = userData.extra_permissions
                        .filter(perm => perm.is_aggregation === true)
                        .map(perm => ({
                            ...perm.permission,
                            is_aggregation: perm.is_aggregation
                        }));
                    console.log('user:', userData);
                    setRight(activeExtraPermissions);

                    if (permissions.status === 'fulfilled') {
                        const permissionData = permissions.value?.data || [];
                        // filtra los permisos que ya se encuentran asignados
                        const filteredPermissions = permissionData.filter(perm =>
                            !activeExtraPermissions.some(p => p.permission_id === perm.permission_id));
                        console.log('permissions:', permissionData);
                        setLeft(filteredPermissions);

                    }
                }


            } catch (error) {
                console.error('Error fetching permissions:', error.message);
            } finally {
                setLoading(false);
            }
        };

        if (open && selected.length > 0) {
            request();
        }
    }, [open, selected]);



    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        const filteredLeftChecked = getFilteredLeftChecked();
        // Encuentra los permisos completos que corresponden a los IDs filtrados
        const permissionsToMove = left.filter(perm =>
            filteredLeftChecked.includes(perm.permission_id)
        );
        setRight([...right, ...permissionsToMove]);
        setLeft(left.filter(perm => !filteredLeftChecked.includes(perm.permission_id)));
        setChecked(checked.filter(id => !filteredLeftChecked.includes(id)));
    };

    const handleCheckedLeft = () => {
        const filteredRightChecked = getFilteredRightChecked();
        const permissionsToMove = right.filter(perm =>
            filteredRightChecked.includes(perm.permission_id)
        );
        setLeft([...left, ...permissionsToMove]);
        setRight(right.filter(perm => !filteredRightChecked.includes(perm.permission_id)));
        setChecked(checked.filter(id => !filteredRightChecked.includes(id)));
    };

    const clearForm = () => {
        setChecked([]);
        setLeft([]);
        setRight([]);
        setLeftSearch('');
        setRightSearch('');
        setOpen(false);
    };


    // Agrupa permisos por el prefijo de 'name'
    const groupPermissions = (permissions) => {
        return permissions.reduce((acc, perm) => {
            // Verifica que perm.name sea una cadena válida
            const category = typeof perm.name === 'string' && perm.name.includes('.')
                ? perm.name.split('.')[0]
                : 'Uncategorized'; // Usa una categoría por defecto si no es válida

            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(perm);
            return acc;
        }, {});
    };

    const getFilteredLeftChecked = () => {
        const groupedPermissions = groupPermissions(left);
        const filteredGroups = Object.entries(groupedPermissions).reduce((result, [group, perms]) => {
            const filteredPerms = perms.filter(
                (perm) => typeof perm.name === 'string' &&
                perm.name.toLowerCase().includes(leftSearch.toLowerCase())
            );
            if (filteredPerms.length > 0) {
                result[group] = filteredPerms;
            }
            return result;
        }, {});

        return intersection(checked, Object.values(filteredGroups).flat().map(p => p.permission_id));
    };


    const getFilteredRightChecked = () => {
        const groupedPermissions = groupPermissions(right);
        const filteredGroups = Object.entries(groupedPermissions).reduce((result, [group, perms]) => {
            const filteredPerms = perms.filter(
                (perm) => typeof perm.name === 'string' &&
                perm.name.toLowerCase().includes(rightSearch.toLowerCase())
            );
            if (filteredPerms.length > 0) {
                result[group] = filteredPerms;
            }
            return result;
        }, {});

        return intersection(checked, Object.values(filteredGroups).flat().map(p => p.permission_id));
    };


    const customList = (title, permissions, searchValue, setSearchValue) => {

        const groupedPermissions = groupPermissions(permissions);

        const filteredGroups = Object.entries(groupedPermissions).reduce((result, [group, perms]) => {
            const filteredPerms = perms.filter(
                (perm) => typeof perm.name === 'string' && perm.name.toLowerCase().includes(searchValue.toLowerCase())
            );
            if (filteredPerms.length > 0) {
                result[group] = filteredPerms;
            }
            return result;
        }, {});

        return (

            <Card>
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    title={title}
                    subheader={`${permissions.length} ${t('user_management.table.dialog.extra_permission.permissions')}`}
                />
                <Divider />
                <TextField
                    placeholder={t('user_management.table.dialog.extra_permission.search')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ px: 2, py: 1 }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                />
                <List
                    sx={{
                        width: '100%',
                        height: 300,
                        maxWidth: 360,
                        bgcolor: 'background.paper',
                        position: 'relative',
                        overflow: 'auto',
                        maxHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    subheader={<li />}

                >
                    { loading ? <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <CircularProgress size={25} color="inherit" />
                                </div> : Object.entries(filteredGroups).map(([group, perms]) => (
                                <React.Fragment key={group}>
                                    <ListSubheader>{group}</ListSubheader>
                                    {perms.map((perm) => (
                                        <ListItemButton
                                            key={perm.permission_id}
                                            role="listitem"
                                            onClick={handleToggle(perm.permission_id)}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={checked.includes(perm.permission_id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={perm.name.split('.').pop()}
                                                secondary={perm.description || 'No description'}
                                            />
                                        </ListItemButton>
                                    ))}
                                </React.Fragment>
                            ))}
                </List>
            </Card>
        );
    };



    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='md'>
            <DialogTitle>{t('user_management.table.dialog.extra_permission.title')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => clearForm()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Grid
                    container
                    spacing={2}
                    sx={{ justifyContent: 'center', alignItems: 'center' }}
                >
                    <Grid item xs={5}>{customList(t('user_management.table.dialog.extra_permission.available_list'), left, leftSearch, setLeftSearch)}</Grid>
                    <Grid item>
                        <Grid container direction="column">
                            <Button
                                sx={{ my: 0.5 }}
                                variant="outlined"
                                size="small"
                                onClick={handleCheckedRight}
                                disabled={getFilteredLeftChecked().length === 0}
                                aria-label="move selected right"
                            >
                                &gt;
                            </Button>
                            <Button
                                sx={{ my: 0.5 }}
                                variant="outlined"
                                size="small"
                                onClick={handleCheckedLeft}
                                disabled={getFilteredRightChecked().length === 0}
                                aria-label="move selected left"
                            >
                                &lt;
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid item xs={5}>{customList(t('user_management.table.dialog.extra_permission.assigned_list'), right, rightSearch, setRightSearch)}</Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => clearForm()} color="error" variant='outlined'>
                    {t('user_management.table.dialog.extra_permission.buttons.cancel')}
                </Button>
                <LoadingButton loading={loading} onClick={ async () => {

                    try{

                        const permissions = [];
                        right.forEach(perm => {
                            permissions.push({ name: perm.name,  permission_id: perm.permission_id });
                        });

                        let body = {
                            permissions: permissions
                        };
                        console.log('Extra permissions:', body);

                        const response = await assignExtraPermissions(setLoading, body, selected[0]);

                        if (response.status === 200){
                            setAlert({ msg: t('user_management.table.dialog.extra_permission.messages.success'), severity: 'success' });
                            setOpenAlert(true);
                            setContextMenu(false);
                            setSelectedRow([]);
                            clearForm();
                            reloading();
                        }

                    }catch(error){
                        setAlert({ msg: t('user_management.table.dialog.extra_permission.messages.error'), severity: 'error' });
                        setOpenAlert(true);
                        console.error('Error assigning extra permissions:', error.message);
                    }
                }} color="primary"
                variant='contained'
                >
                    {t('user_management.table.dialog.extra_permission.buttons.assign')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

export { ExtraPermission };